import React, { createContext, useContext, useState, useEffect } from 'react';
import { autoTranslate, onTranslations } from '@/lib/autoTranslate';
import { detectLanguage } from '@/lib/detectLanguage';

export const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', dir: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', dir: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', dir: 'ltr' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', dir: 'ltr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', dir: 'ltr' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', dir: 'ltr' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', dir: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिंदी', dir: 'ltr' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', dir: 'ltr' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', dir: 'ltr' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', dir: 'ltr' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', dir: 'ltr' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', dir: 'ltr' },
  { code: 'th', name: 'Thai', nativeName: 'ภาษาไทย', dir: 'ltr' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', dir: 'ltr' },
];

// Static translations — no LLM credits needed
const TRANSLATIONS = {
  es: {
    'Dashboard': 'Panel',
    'My Profile': 'Mi Perfil',
    'User Management': 'Gestión de Usuarios',
    'Audit Logs': 'Registros de Auditoría',
    'APIs & AI': 'APIs e IA',
    'Security Center': 'Centro de Seguridad',
    'Messages': 'Mensajes',
    'Sign Out': 'Cerrar Sesión',
    'Online': 'En línea',
    'Profile Photo': 'Foto de Perfil',
    'Personal Information': 'Información Personal',
    'Full Name': 'Nombre Completo',
    'Email': 'Correo',
    'Phone': 'Teléfono',
    'Job Title': 'Cargo',
    'Department': 'Departamento',
    'Language': 'Idioma',
    'Save Changes': 'Guardar Cambios',
    'Saving...': 'Guardando...',
    'Change Password': 'Cambiar Contraseña',
    'New Password': 'Nueva Contraseña',
    'Confirm Password': 'Confirmar Contraseña',
    'Cancel': 'Cancelar',
    'Change': 'Cambiar',
    'Changing...': 'Cambiando...',
    'Profile updated': 'Perfil actualizado',
    'Your profile has been saved successfully.': 'Tu perfil se guardó correctamente.',
    'Error': 'Error',
    'Failed to save profile.': 'Error al guardar perfil.',
    'Photo updated': 'Foto actualizada',
    'Upload failed': 'Error al subir',
    'Success': 'Éxito',
    'Your password has been changed successfully.': 'Tu contraseña fue cambiada correctamente.',
    'Failed to change password. Please try again.': 'Error al cambiar contraseña. Inténtalo de nuevo.',
    'Passwords do not match': 'Las contraseñas no coinciden',
    'Password must be at least 6 characters': 'La contraseña debe tener al menos 6 caracteres',
    'Email is managed by the platform': 'El email es gestionado por la plataforma',
    'e.g. Software Engineer': 'ej. Ingeniero de Software',
    'e.g. Engineering': 'ej. Ingeniería',
    'Enter new password': 'Nueva contraseña',
    'Confirm new password': 'Confirmar contraseña',
    'Click "Change" to update your password': 'Haz clic en "Cambiar" para actualizar tu contraseña',
    'Invite User': 'Invitar Usuario',
    'registered users': 'usuarios registrados',
    'Search by name or email...': 'Buscar por nombre o email...',
    'All Roles': 'Todos los Roles',
    'administrator': 'Administrador',
    'operator': 'Operador',
    'user': 'Usuario',
    'User': 'Usuario',
    'Role': 'Rol',
    'Status': 'Estado',
    'Position': 'Cargo',
    'Last Seen': 'Última vez',
    'Loading...': 'Cargando...',
    'No users found': 'No se encontraron usuarios',
    'Permission denied': 'Permiso denegado',
    'Operators can only manage regular users': 'Los operadores solo pueden gestionar usuarios regulares',
    'Operators can only modify regular users': 'Los operadores solo pueden modificar usuarios regulares',
    'Action completed': 'Acción completada',
    'Role updated': 'Rol actualizado',
    'Delete this user? This cannot be undone.': '¿Eliminar este usuario? Esto no se puede deshacer.',
    'active': 'activo',
    'suspended': 'suspendido',
    'pending': 'pendiente',
    'Compose': 'Redactar',
    'Inbox': 'Bandeja de entrada',
    'Sent': 'Enviados',
    'New Message': 'Nuevo Mensaje',
    'To': 'Para',
    'Subject': 'Asunto',
    'Message': 'Mensaje',
    'Send': 'Enviar',
    'Sending...': 'Enviando...',
    'Reply': 'Responder',
    'Delete': 'Eliminar',
    'No messages': 'Sin mensajes',
    'Unread': 'No leído',
    'Read': 'Leído',
    'Contact Messages': 'Mensajes de Contacto',
    'Name': 'Nombre',
    'Company': 'Empresa',
    'From': 'De',
    'Date': 'Fecha',
    'Mark as read': 'Marcar como leído',
    'All': 'Todos',
    'Welcome back': 'Bienvenido de nuevo',
    'Total Users': 'Usuarios Totales',
    'Audit Events': 'Eventos de Auditoría',
    'Security Reports': 'Reportes de Seguridad',
    'AI Integrations': 'Integraciones de IA',
    'Quick Access': 'Acceso Rápido',
    'Edit My Profile': 'Editar Mi Perfil',
    'Manage AI Integrations': 'Gestionar Integraciones de IA',
    'Manage Users': 'Gestionar Usuarios',
    'Recent Activity': 'Actividad Reciente',
    'Open Issues': 'Incidencias Abiertas',
    'Critical': 'Crítico',
    'Blocked Attacks': 'Ataques Bloqueados',
    'Resolved': 'Resueltos',
    'Overview': 'Resumen',
    'Vulnerabilities': 'Vulnerabilidades',
    'Attacks': 'Ataques',
    'Code Issues': 'Problemas de Código',
    'AI Consultation': 'Consulta de IA',
    'Sample report added': 'Reporte de ejemplo añadido',
    'AI Integrations Status': 'Estado de Integraciones de IA',
    'No cybersecurity AI connected': 'Sin IA de ciberseguridad conectada',
    'Connect one now →': 'Conectar una ahora →',
    'Add Sample Reports': 'Añadir Reportes de Ejemplo',
    'Simulate security events for testing': 'Simular eventos de seguridad para pruebas',
    'Add Vulnerability': 'Añadir Vulnerabilidad',
    'Add Attack Attempt': 'Añadir Intento de Ataque',
    'Add Code Issue': 'Añadir Problema de Código',
    'High Priority Issues': 'Incidencias de Alta Prioridad',
    'No vulnerabilities detected': 'No se detectaron vulnerabilidades',
    'No attack attempts recorded': 'No se registraron intentos de ataque',
    'Type': 'Tipo',
    'IP': 'IP',
    'Country': 'País',
    'No code issues found': 'No se encontraron problemas de código',
    'AI Security Consultation': 'Consulta de Seguridad con IA',
    "Ask your security AI anything about the platform's threat landscape": 'Pregunta a tu IA de seguridad sobre el panorama de amenazas de la plataforma',
    'Analyze current vulnerabilities': 'Analizar vulnerabilidades actuales',
    'What are the biggest threats?': '¿Cuáles son las mayores amenazas?',
    'How can I improve security?': '¿Cómo puedo mejorar la seguridad?',
    'Summarize recent attack attempts': 'Resumir intentos de ataque recientes',
    'Security AI': 'IA de Seguridad',
    'Analyzing...': 'Analizando...',
    'Ask the security AI...': 'Pregúntale a la IA de seguridad...',
    'Monitored by': 'Monitoreado por',
    'events': 'eventos',
    'All Categories': 'Todas las Categorías',
    'Search logs...': 'Buscar registros...',
    'All Severities': 'Todas las Severidades',
    'No logs found': 'No se encontraron registros',
    'Timestamp': 'Fecha y Hora',
    'Action': 'Acción',
    'Category': 'Categoría',
    'Severity': 'Severidad',
    'APIs & AI Integrations': 'APIs e Integraciones de IA',
    'Connect external AI services to the platform': 'Conecta servicios de IA externos a la plataforma',
    'Add Integration': 'Añadir Integración',
    'About AI Integrations': 'Sobre las Integraciones de IA',
    'When connecting a Cybersecurity AI, all capabilities are accepted and the AI will have access to security monitoring, code scanning, and threat blocking. Integration changes suggested by Security AIs will be applied automatically.': 'Al conectar una IA de Ciberseguridad, se aceptan todas sus capacidades y la IA tendrá acceso al monitoreo de seguridad, escaneo de código y bloqueo de amenazas. Los cambios de integración sugeridos por las IAs de Seguridad se aplicarán automáticamente.',
    'No integrations yet': 'Aún no hay integraciones',
    'Connect your first AI service to enhance the platform.': 'Conecta tu primer servicio de IA para mejorar la plataforma.',
    'Add First Integration': 'Añadir Primera Integración',
    'Remove this integration?': '¿Eliminar esta integración?',
    'Integration removed': 'Integración eliminada',
    'Permissions Accepted': 'Permisos Aceptados',
    'Last sync': 'Última sincronización',
    'Active': 'Activo',
    'Inactive': 'Inactivo',
    'Chat with AI': 'Chatear con la IA',
    'Security Dashboard': 'Panel de Seguridad',
    'Operators can only edit regular users': 'Los operadores solo pueden editar usuarios regulares',
    'You cannot change your own role or status.': 'No puedes cambiar tu propio rol o estado.',
    'Password updated': 'Contraseña actualizada',
    'Password changed successfully for this user.': 'La contraseña se cambió correctamente para este usuario.',
    'Administrators can reset passwords for any user': 'Los administradores pueden restablecer la contraseña de cualquier usuario',
    'User updated successfully': 'Usuario actualizado correctamente',
    'Unknown User': 'Usuario Desconocido',
    'Change User Password': 'Cambiar Contraseña del Usuario',
    'Email Address': 'Correo Electrónico',
    'Invite New Member': 'Invitar Nuevo Miembro',
    'Full access. Can manage users, APIs, and all system settings.': 'Acceso total. Puede gestionar usuarios, APIs y toda la configuración del sistema.',
    'Can manage APIs, view logs, and access security center.': 'Puede gestionar APIs, ver registros y acceder al centro de seguridad.',
    'Standard access. Can view their profile and basic features.': 'Acceso estándar. Puede ver su perfil y funciones básicas.',
    'Send Invitation': 'Enviar Invitación',
    'Invitation sent': 'Invitación enviada',
    'Invitation sent to': 'Invitación enviada a',
    'Ecosystem': 'Ecosistema',
    'Ventures': 'Empresas',
    'Vision': 'Visión',
    'Contact': 'Contacto',
    'Staff Login': 'Acceso Personal',
    'Portal': 'Portal',
  },
  fr: {
    'Dashboard': 'Tableau de bord',
    'My Profile': 'Mon Profil',
    'User Management': 'Gestion des Utilisateurs',
    'Audit Logs': "Journaux d'Audit",
    'APIs & AI': 'APIs et IA',
    'Security Center': 'Centre de Sécurité',
    'Messages': 'Messages',
    'Sign Out': 'Déconnexion',
    'Online': 'En ligne',
    'Profile Photo': 'Photo de Profil',
    'Personal Information': 'Informations Personnelles',
    'Full Name': 'Nom Complet',
    'Email': 'E-mail',
    'Phone': 'Téléphone',
    'Job Title': 'Poste',
    'Department': 'Département',
    'Language': 'Langue',
    'Save Changes': 'Enregistrer',
    'Saving...': 'Enregistrement...',
    'Change Password': 'Changer le Mot de Passe',
    'New Password': 'Nouveau Mot de Passe',
    'Confirm Password': 'Confirmer le Mot de Passe',
    'Cancel': 'Annuler',
    'Change': 'Changer',
    'Changing...': 'Changement...',
    'Profile updated': 'Profil mis à jour',
    'Your profile has been saved successfully.': 'Votre profil a été enregistré avec succès.',
    'Error': 'Erreur',
    'Failed to save profile.': "Échec de l'enregistrement du profil.",
    'Photo updated': 'Photo mise à jour',
    'Upload failed': "Échec de l'envoi",
    'Success': 'Succès',
    'Your password has been changed successfully.': 'Votre mot de passe a été changé avec succès.',
    'Failed to change password. Please try again.': 'Échec du changement de mot de passe. Réessayez.',
    'Passwords do not match': 'Les mots de passe ne correspondent pas',
    'Password must be at least 6 characters': 'Le mot de passe doit contenir au moins 6 caractères',
    'Email is managed by the platform': "L'e-mail est géré par la plateforme",
    'e.g. Software Engineer': 'ex. Ingénieur Logiciel',
    'e.g. Engineering': 'ex. Ingénierie',
    'Enter new password': 'Nouveau mot de passe',
    'Confirm new password': 'Confirmer le mot de passe',
    'Click "Change" to update your password': 'Cliquez sur "Changer" pour mettre à jour votre mot de passe',
    'Invite User': 'Inviter un Utilisateur',
    'registered users': 'utilisateurs enregistrés',
    'Search by name or email...': 'Rechercher par nom ou e-mail...',
    'All Roles': 'Tous les Rôles',
    'administrator': 'Administrateur',
    'operator': 'Opérateur',
    'user': 'Utilisateur',
    'User': 'Utilisateur',
    'Role': 'Rôle',
    'Status': 'Statut',
    'Position': 'Poste',
    'Last Seen': 'Vu Pour la Dernière Fois',
    'Loading...': 'Chargement...',
    'No users found': 'Aucun utilisateur trouvé',
    'Permission denied': 'Permission refusée',
    'Operators can only manage regular users': 'Les opérateurs ne peuvent gérer que les utilisateurs réguliers',
    'Operators can only modify regular users': 'Les opérateurs ne peuvent modifier que les utilisateurs réguliers',
    'Action completed': 'Action terminée',
    'Role updated': 'Rôle mis à jour',
    'Delete this user? This cannot be undone.': 'Supprimer cet utilisateur ? Cette action est irréversible.',
    'active': 'actif',
    'suspended': 'suspendu',
    'pending': 'en attente',
    'Compose': 'Rédiger',
    'Inbox': 'Boîte de Réception',
    'Sent': 'Envoyés',
    'New Message': 'Nouveau Message',
    'To': 'À',
    'Subject': 'Sujet',
    'Message': 'Message',
    'Send': 'Envoyer',
    'Sending...': 'Envoi...',
    'Reply': 'Répondre',
    'Delete': 'Supprimer',
    'No messages': 'Aucun message',
    'Unread': 'Non lu',
    'Read': 'Lu',
    'Contact Messages': 'Messages de Contact',
    'Name': 'Nom',
    'Company': 'Entreprise',
    'From': 'De',
    'Date': 'Date',
    'Mark as read': 'Marquer comme lu',
    'All': 'Tous',
    'Welcome back': 'Bon retour',
    'Total Users': "Total d'Utilisateurs",
    'Audit Events': "Événements d'Audit",
    'Security Reports': 'Rapports de Sécurité',
    'AI Integrations': "Intégrations d'IA",
    'Quick Access': 'Accès Rapide',
    'Edit My Profile': 'Modifier Mon Profil',
    'Manage AI Integrations': "Gérer les Intégrations d'IA",
    'Manage Users': 'Gérer les Utilisateurs',
    'Recent Activity': 'Activité Récente',
    'Open Issues': 'Problèmes Ouverts',
    'Critical': 'Critique',
    'Blocked Attacks': 'Attaques Bloquées',
    'Resolved': 'Résolus',
    'Overview': 'Aperçu',
    'Vulnerabilities': 'Vulnérabilités',
    'Attacks': 'Attaques',
    'Code Issues': 'Problèmes de Code',
    'AI Consultation': 'Consultation IA',
    'No cybersecurity AI connected': 'Aucune IA de cybersécurité connectée',
    'Type': 'Type',
    'IP': 'IP',
    'Country': 'Pays',
    'Active': 'Actif',
    'Inactive': 'Inactif',
    'Add Integration': 'Ajouter une Intégration',
    'Ecosystem': 'Écosystème',
    'Ventures': 'Entreprises',
    'Vision': 'Vision',
    'Contact': 'Contact',
    'Staff Login': 'Connexion du Personnel',
    'Portal': 'Portail',
  },
  de: {
    'Dashboard': 'Übersicht',
    'My Profile': 'Mein Profil',
    'User Management': 'Benutzerverwaltung',
    'Audit Logs': 'Prüfprotokolle',
    'APIs & AI': 'APIs & KI',
    'Security Center': 'Sicherheitszentrum',
    'Messages': 'Nachrichten',
    'Sign Out': 'Abmelden',
    'Online': 'Online',
    'Profile Photo': 'Profilbild',
    'Personal Information': 'Persönliche Informationen',
    'Full Name': 'Vollständiger Name',
    'Email': 'E-Mail',
    'Phone': 'Telefon',
    'Job Title': 'Position',
    'Department': 'Abteilung',
    'Language': 'Sprache',
    'Save Changes': 'Speichern',
    'Saving...': 'Speichern...',
    'Change Password': 'Passwort Ändern',
    'New Password': 'Neues Passwort',
    'Confirm Password': 'Passwort Bestätigen',
    'Cancel': 'Abbrechen',
    'Change': 'Ändern',
    'Changing...': 'Ändern...',
    'Profile updated': 'Profil aktualisiert',
    'Your profile has been saved successfully.': 'Ihr Profil wurde erfolgreich gespeichert.',
    'Error': 'Fehler',
    'Failed to save profile.': 'Profil konnte nicht gespeichert werden.',
    'Photo updated': 'Foto aktualisiert',
    'Upload failed': 'Upload fehlgeschlagen',
    'Success': 'Erfolg',
    'Your password has been changed successfully.': 'Ihr Passwort wurde erfolgreich geändert.',
    'Failed to change password. Please try again.': 'Passwort konnte nicht geändert werden. Bitte erneut versuchen.',
    'Passwords do not match': 'Passwörter stimmen nicht überein',
    'Password must be at least 6 characters': 'Das Passwort muss mindestens 6 Zeichen lang sein',
    'Email is managed by the platform': 'Die E-Mail wird von der Plattform verwaltet',
    'e.g. Software Engineer': 'z. B. Software-Ingenieur',
    'e.g. Engineering': 'z. B. Technik',
    'Enter new password': 'Neues Passwort eingeben',
    'Confirm new password': 'Passwort bestätigen',
    'Click "Change" to update your password': 'Klicken Sie auf "Ändern", um Ihr Passwort zu aktualisieren',
    'Invite User': 'Benutzer Einladen',
    'registered users': 'registrierte Benutzer',
    'Search by name or email...': 'Nach Name oder E-Mail suchen...',
    'All Roles': 'Alle Rollen',
    'administrator': 'Administrator',
    'operator': 'Operator',
    'user': 'Benutzer',
    'User': 'Benutzer',
    'Role': 'Rolle',
    'Status': 'Status',
    'Position': 'Position',
    'Last Seen': 'Zuletzt Gesehen',
    'Loading...': 'Wird geladen...',
    'No users found': 'Keine Benutzer gefunden',
    'Permission denied': 'Zugriff verweigert',
    'Operators can only manage regular users': 'Operatoren können nur reguläre Benutzer verwalten',
    'Operators can only modify regular users': 'Operatoren können nur reguläre Benutzer ändern',
    'Action completed': 'Aktion abgeschlossen',
    'Role updated': 'Rolle aktualisiert',
    'Delete this user? This cannot be undone.': 'Diesen Benutzer löschen? Dies kann nicht rückgängig gemacht werden.',
    'active': 'aktiv',
    'suspended': 'gesperrt',
    'pending': 'ausstehend',
    'Compose': 'Verfassen',
    'Inbox': 'Posteingang',
    'Sent': 'Gesendet',
    'New Message': 'Neue Nachricht',
    'To': 'An',
    'Subject': 'Betreff',
    'Message': 'Nachricht',
    'Send': 'Senden',
    'Sending...': 'Senden...',
    'Reply': 'Antworten',
    'Delete': 'Löschen',
    'No messages': 'Keine Nachrichten',
    'Unread': 'Ungelesen',
    'Read': 'Gelesen',
    'Contact Messages': 'Kontaktnachrichten',
    'Name': 'Name',
    'Company': 'Unternehmen',
    'From': 'Von',
    'Date': 'Datum',
    'Mark as read': 'Als gelesen markieren',
    'All': 'Alle',
    'Welcome back': 'Willkommen zurück',
    'Total Users': 'Benutzer Gesamt',
    'Audit Events': 'Prüfereignisse',
    'Security Reports': 'Sicherheitsberichte',
    'AI Integrations': 'KI-Integrationen',
    'Quick Access': 'Schnellzugriff',
    'Edit My Profile': 'Mein Profil Bearbeiten',
    'Manage AI Integrations': 'KI-Integrationen Verwalten',
    'Manage Users': 'Benutzer Verwalten',
    'Recent Activity': 'Letzte Aktivität',
    'Open Issues': 'Offene Probleme',
    'Critical': 'Kritisch',
    'Blocked Attacks': 'Blockierte Angriffe',
    'Resolved': 'Gelöst',
    'Overview': 'Übersicht',
    'Vulnerabilities': 'Schwachstellen',
    'Attacks': 'Angriffe',
    'Code Issues': 'Code-Probleme',
    'AI Consultation': 'KI-Beratung',
    'No cybersecurity AI connected': 'Keine Cybersicherheits-KI verbunden',
    'Type': 'Typ',
    'IP': 'IP',
    'Country': 'Land',
    'Active': 'Aktiv',
    'Inactive': 'Inaktiv',
    'Add Integration': 'Integration Hinzufügen',
    'Ecosystem': 'Ökosystem',
    'Ventures': 'Unternehmen',
    'Vision': 'Vision',
    'Contact': 'Kontakt',
    'Staff Login': 'Mitarbeiter-Login',
    'Portal': 'Portal',
  },
  pt: {
    'Dashboard': 'Painel',
    'My Profile': 'Meu Perfil',
    'User Management': 'Gestão de Usuários',
    'Audit Logs': 'Registros de Auditoria',
    'APIs & AI': 'APIs e IA',
    'Security Center': 'Centro de Segurança',
    'Messages': 'Mensagens',
    'Sign Out': 'Sair',
    'Online': 'Online',
    'Profile Photo': 'Foto de Perfil',
    'Personal Information': 'Informações Pessoais',
    'Full Name': 'Nome Completo',
    'Email': 'E-mail',
    'Phone': 'Telefone',
    'Job Title': 'Cargo',
    'Department': 'Departamento',
    'Language': 'Idioma',
    'Save Changes': 'Salvar',
    'Saving...': 'Salvando...',
    'Change Password': 'Alterar Senha',
    'New Password': 'Nova Senha',
    'Confirm Password': 'Confirmar Senha',
    'Cancel': 'Cancelar',
    'Change': 'Alterar',
    'Changing...': 'Alterando...',
    'Profile updated': 'Perfil atualizado',
    'Your profile has been saved successfully.': 'Seu perfil foi salvo com sucesso.',
    'Error': 'Erro',
    'Failed to save profile.': 'Falha ao salvar perfil.',
    'Photo updated': 'Foto atualizada',
    'Upload failed': 'Falha no envio',
    'Success': 'Sucesso',
    'Your password has been changed successfully.': 'Sua senha foi alterada com sucesso.',
    'Failed to change password. Please try again.': 'Falha ao alterar senha. Tente novamente.',
    'Passwords do not match': 'As senhas não coincidem',
    'Password must be at least 6 characters': 'A senha deve ter pelo menos 6 caracteres',
    'Email is managed by the platform': 'O e-mail é gerenciado pela plataforma',
    'e.g. Software Engineer': 'ex. Engenheiro de Software',
    'e.g. Engineering': 'ex. Engenharia',
    'Enter new password': 'Digite a nova senha',
    'Confirm new password': 'Confirme a nova senha',
    'Click "Change" to update your password': 'Clique em "Alterar" para atualizar sua senha',
    'Invite User': 'Convidar Usuário',
    'registered users': 'usuários registrados',
    'Search by name or email...': 'Buscar por nome ou e-mail...',
    'All Roles': 'Todas as Funções',
    'administrator': 'Administrador',
    'operator': 'Operador',
    'user': 'Usuário',
    'User': 'Usuário',
    'Role': 'Função',
    'Status': 'Status',
    'Position': 'Cargo',
    'Last Seen': 'Visto Por Último',
    'Loading...': 'Carregando...',
    'No users found': 'Nenhum usuário encontrado',
    'Permission denied': 'Permissão negada',
    'Operators can only manage regular users': 'Operadores só podem gerenciar usuários regulares',
    'Operators can only modify regular users': 'Operadores só podem modificar usuários regulares',
    'Action completed': 'Ação concluída',
    'Role updated': 'Função atualizada',
    'Delete this user? This cannot be undone.': 'Excluir este usuário? Isso não pode ser desfeito.',
    'active': 'ativo',
    'suspended': 'suspenso',
    'pending': 'pendente',
    'Compose': 'Escrever',
    'Inbox': 'Caixa de Entrada',
    'Sent': 'Enviados',
    'New Message': 'Nova Mensagem',
    'To': 'Para',
    'Subject': 'Assunto',
    'Message': 'Mensagem',
    'Send': 'Enviar',
    'Sending...': 'Enviando...',
    'Reply': 'Responder',
    'Delete': 'Excluir',
    'No messages': 'Sem mensagens',
    'Unread': 'Não lido',
    'Read': 'Lido',
    'Contact Messages': 'Mensagens de Contato',
    'Name': 'Nome',
    'Company': 'Empresa',
    'From': 'De',
    'Date': 'Data',
    'Mark as read': 'Marcar como lido',
    'All': 'Todos',
    'Welcome back': 'Bem-vindo de volta',
    'Total Users': 'Total de Usuários',
    'Audit Events': 'Eventos de Auditoria',
    'Security Reports': 'Relatórios de Segurança',
    'AI Integrations': 'Integrações de IA',
    'Quick Access': 'Acesso Rápido',
    'Edit My Profile': 'Editar Meu Perfil',
    'Manage AI Integrations': 'Gerenciar Integrações de IA',
    'Manage Users': 'Gerenciar Usuários',
    'Recent Activity': 'Atividade Recente',
    'Open Issues': 'Problemas Abertos',
    'Critical': 'Crítico',
    'Blocked Attacks': 'Ataques Bloqueados',
    'Resolved': 'Resolvidos',
    'Overview': 'Visão Geral',
    'Vulnerabilities': 'Vulnerabilidades',
    'Attacks': 'Ataques',
    'Code Issues': 'Problemas de Código',
    'AI Consultation': 'Consulta de IA',
    'No cybersecurity AI connected': 'Nenhuma IA de segurança conectada',
    'Type': 'Tipo',
    'IP': 'IP',
    'Country': 'País',
    'Active': 'Ativo',
    'Inactive': 'Inativo',
    'Add Integration': 'Adicionar Integração',
    'Ecosystem': 'Ecossistema',
    'Ventures': 'Empresas',
    'Vision': 'Visão',
    'Contact': 'Contato',
    'Staff Login': 'Login da Equipe',
    'Portal': 'Portal',
  },
};

// Fixed Spanish names for the full service catalog (shown instantly, no auto-translation delay).
const SERVICES_ES = {
  'Websites': 'Sitios web', 'Web Development': 'Desarrollo web',
  'Landing Pages': 'Landing pages', 'Conversion-Focused': 'Enfocadas en conversión',
  'SaaS Platforms': 'Plataformas SaaS', 'Scalable Platforms': 'Plataformas escalables',
  'Mobile Applications': 'Aplicaciones móviles', 'iOS & Android': 'iOS y Android',
  'Games': 'Videojuegos', 'Interactive Entertainment': 'Entretenimiento interactivo',
  'Custom AI': 'IA a medida', 'Tailored Intelligence': 'Inteligencia personalizada',
  'Business Automations': 'Automatización empresarial', 'Operational Efficiency': 'Eficiencia operativa',
  'Digital Marketing': 'Marketing digital', 'Growth & Reach': 'Crecimiento y alcance',
  'Cybersecurity': 'Ciberseguridad', 'Powered by 0Logic AI': 'Impulsado por 0Logic AI',
  'What We Build for Clients': 'Lo que creamos para nuestros clientes',
  'Services for Businesses in Dubai & the UAE': 'Servicios para empresas en Dubái y EAU',
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [storedLang, setLanguageState] = useState(() => {
    return localStorage.getItem('jotunheims_lang') || detectLanguage();
  });
  const [pathLang, setPathLang] = useState(null);
  const [, setTick] = useState(0);
  const language = pathLang || storedLang;

  useEffect(() => onTranslations(() => setTick((n) => n + 1)), []);

  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = currentLang.dir;
  }, [language, currentLang]);

  const t = (text) => {
    if (!text || language === 'en' || typeof text !== 'string') return text;
    const fixed = (language === 'es' && SERVICES_ES[text]) || TRANSLATIONS[language]?.[text];
    if (fixed) return fixed;
    return language === 'es' ? autoTranslate(text) : text;
  };

  const setLanguage = (code) => {
    localStorage.setItem('jotunheims_lang', code);
    setLanguageState(code);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, setPathLang, pathLang, t, languages: LANGUAGES, currentLang, isTranslating: false }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used within LanguageProvider');
  return ctx;
};